import { AnimatePresence, motion } from "framer-motion";
import { Brain, Building, CircuitBoard, Clock, Cog, Cpu, Globe, Megaphone, MessageSquare, Monitor, Rocket, Settings, Target, Wrench } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Route, Routes } from "react-router-dom";
import AICourseDetails from "./AICourseDetails";
import ApplicationForm from "./ApplicationForm";
import Brochure from "./Brochure";
import CourseDetails from "./CourseDetails";
import DigitalMarketingCourseDetails from "./DigitalMarketingCourseDetails";
import IndustrialDesignCourseDetails from "./IndustrialDesignCourseDetails";
import Preloader from "./Preloader";
import SMTAssemblyCourseDetails from "./SMTAssemblyCourseDetails";
import Workshops from "./Workshops";

export default function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <Preloader key="preloader" />
      ) : (
        <Routes>
           <Route path="/" element={<Home />} />
           <Route path="/workshops" element={<Workshops />} />
           <Route path="/hardware-course" element={<CourseDetails />} />
           <Route path="/ai-course" element={<AICourseDetails />} />
           <Route path="/digital-marketing-course" element={<DigitalMarketingCourseDetails />} />
           <Route path="/smt-assembly-course" element={<SMTAssemblyCourseDetails />} />
           <Route path="/aerospace-course" element={<IndustrialDesignCourseDetails />} />
           <Route path="/brochure" element={<Brochure />} />
           <Route path="/apply" element={<ApplicationForm />} />
           <Route path="/privacy-policy" element={<PrivacyPolicy />} />
           <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
         </Routes>
      )}
    </AnimatePresence>
  );
}

function Home() {
  return (
    <motion.div
      key="main"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900 selection:bg-indigo-200/60 relative z-10"
    >
      <div className="fixed inset-0 pointer-events-none z-0">
        <svg className="absolute top-20 left-10 w-32 h-32 opacity-10 text-indigo-500" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="2"/>
        </svg>
        <svg className="absolute bottom-40 right-20 w-48 h-48 opacity-5 text-violet-500" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <polygon points="50,5 90,90 10,90" fill="currentColor" opacity="0.3"/>
        </svg>
        <svg className="absolute top-1/3 right-1/3 w-64 h-64 opacity-5 text-slate-400" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <rect x="10" y="10" width="180" height="180" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.2"/>
          <circle cx="100" cy="100" r="50" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.3"/>
        </svg>
        <svg className="absolute bottom-1/3 left-1/4 w-40 h-40 opacity-5 text-indigo-400" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M20,20 L80,20 L80,80 L20,80 Z" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.4"/>
          <line x1="50" y1="20" x2="50" y2="80" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
          <line x1="20" y1="50" x2="80" y2="50" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
        </svg>
      </div>
      <SiteHeader />
      <Hero />
      <Highlights />
      <Programs />
      <WorkshopsSection />
      <FlexibleLearning />
      <WhyChooseNCAI />
      <Outcomes />
      <CallToAction />
      <SiteFooter />
      <WhatsAppChat />
    </motion.div>
  );
}

function Logo({ className = "h-9 w-auto" }) {
  return (
    <Link to="/" className="flex items-center gap-2">
      <img src="/logo.png" alt="NCAI" className={className} />
      <span className="text-xl font-extrabold tracking-tight">
      </span>
    </Link>
  );
}

function SiteHeader() {
   const [isMenuOpen, setIsMenuOpen] = useState(false);

   return (
     <header className="sticky top-0 z-50 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-slate-200/60">
       <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
         <div className="flex h-16 items-center justify-between">
           <Link to="/" className="flex items-center gap-3" onClick={() => setIsMenuOpen(false)}>
             <Logo />
             <span className="hidden sm:inline text-sm font-semibold text-slate-700">NeuraCore AI Institute</span>
           </Link>

           {/* Desktop Navigation */}
           <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
             <a href="#programs" className="hover:text-indigo-600 transition-colors">Programs</a>
             <Link to="/workshops" className="hover:text-indigo-600 transition-colors">Workshops</Link>
             <a href="#flex" className="hover:text-indigo-600 transition-colors">Flexible Learning</a>
             <a href="#outcomes" className="hover:text-indigo-600 transition-colors">Outcomes</a>
           </nav>

           <div className="flex items-center gap-3">
             <Link
               to="/apply"
               className="hidden sm:inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 shadow-md hover:shadow-lg transition"
             >
               Apply Now
             </Link>

             {/* Mobile menu button */}
             <button
               onClick={() => setIsMenuOpen(!isMenuOpen)}
               className="md:hidden p-2 rounded-lg text-slate-700 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
               aria-label="Toggle menu"
             >
               <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
               </svg>
             </button>
           </div>
         </div>

         {/* Mobile Navigation */}
         <AnimatePresence>
           {isMenuOpen && (
             <motion.nav
               initial={{ opacity: 0, height: 0 }}
               animate={{ opacity: 1, height: "auto" }}
               exit={{ opacity: 0, height: 0 }}
               transition={{ duration: 0.2 }}
               className="md:hidden border-t border-slate-200/60 bg-white/95 backdrop-blur"
             >
               <div className="px-4 py-4 space-y-3">
                 <a
                   href="#programs"
                   className="block py-2 text-slate-700 hover:text-indigo-600 transition-colors"
                   onClick={() => setIsMenuOpen(false)}
                 >
                   Programs
                 </a>
                 <Link
                   to="/workshops"
                   className="block py-2 text-slate-700 hover:text-indigo-600 transition-colors"
                   onClick={() => setIsMenuOpen(false)}
                 >
                   Workshops
                 </Link>
                 <a
                   href="#flex"
                   className="block py-2 text-slate-700 hover:text-indigo-600 transition-colors"
                   onClick={() => setIsMenuOpen(false)}
                 >
                   Flexible Learning
                 </a>
                 <a
                   href="#outcomes"
                   className="block py-2 text-slate-700 hover:text-indigo-600 transition-colors"
                   onClick={() => setIsMenuOpen(false)}
                 >
                   Outcomes
                 </a>
                 <div className="pt-3 border-t border-slate-200">
                   <Link
                     to="/apply"
                     className="inline-flex items-center justify-center w-full rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 shadow-md hover:shadow-lg transition"
                     onClick={() => setIsMenuOpen(false)}
                   >
                     Apply Now
                   </Link>
                 </div>
               </div>
             </motion.nav>
           )}
         </AnimatePresence>
       </div>
     </header>
   );
 }

function Hero() {
   return (
     <section id="home" className="relative overflow-clip">
       <div className="absolute -inset-x-6 -top-24 h-80 blur-3xl opacity-30 bg-[radial-gradient(60%_60%_at_50%_40%,theme(colors.violet.500),transparent_60%)]" />
       <div className="mx-auto max-w-7xl px-4 pt-14 sm:px-6 lg:px-8">
         <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center py-8 sm:py-12">
           <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
             <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight leading-[1.1]">
               Empower your future with
               <motion.span
                 className="block bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-indigo-600"
                 animate={{
                   x: [0, -2, 2, -2, 2, 0],
                   y: [0, 1, -1, 1, -1, 0]
                 }}
                 transition={{
                   duration: 0.8,
                   repeat: Infinity,
                   repeatDelay: 3,
                   ease: "easeInOut"
                 }}
               >
                 NeuraCore AI Institute
               </motion.span>
             </h1>
             <p className="mt-4 sm:mt-5 text-base sm:text-lg text-slate-600 max-w-2xl">
               Master in‑demand skills across <span className="font-semibold">Digital Marketing</span>, <span className="font-semibold">AI & Machine Learning</span>, and <span className="font-semibold">Hardware Design & Manufacturing</span>. Learn online or in‑person, build portfolio‑ready projects, and get job‑ready in 12 weeks.
             </p>
             <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3">
               <Link to="/apply" className="inline-flex items-center justify-center rounded-2xl px-4 sm:px-5 py-3 font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 shadow-md hover:shadow-lg transition text-sm sm:text-base">
                 Start Application
               </Link>
               <Link to="/brochure" className="inline-flex items-center justify-center rounded-2xl px-4 sm:px-5 py-3 font-semibold border border-slate-300 hover:border-slate-400 text-sm sm:text-base">
                 Download Brochure
               </Link>
               <Link to="/workshops" className="inline-flex items-center justify-center rounded-2xl px-4 sm:px-5 py-3 font-semibold border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 text-sm sm:text-base">
                 Book Workshop
               </Link>
             </div>
             <div className="mt-6 sm:mt-8 grid grid-cols-3 max-w-sm sm:max-w-md text-center gap-3 sm:gap-4">
               <Stat kpi="16+" label="Skill Modules" icon="📚" />
               <Stat kpi="5" label="Core Streams" icon="🌊" />
               <Stat kpi="1" label="Capstone/Stream" icon="🏆" />
             </div>
           </motion.div>
           <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
             <div className="relative aspect-[5/4] rounded-3xl border border-slate-200 shadow-xl bg-white p-3 sm:p-4">
               <div className="absolute -z-10 inset-0 bg-gradient-to-tr from-indigo-100 via-violet-100 to-transparent rounded-3xl" />
               <div className="grid h-full grid-rows-3 gap-3 sm:gap-4">
                 <ProgramPeek title="Digital Marketing" points={["SEO & Google Ads","Social + Content + Email","Analytics & Live Campaign"]} badge="Online / In‑Person" />
                 <ProgramPeek title="AI & Machine Learning" points={["Python + DS Basics","ML & Deep Learning","Generative AI + Capstone"]} badge="Theory: Online / In‑Person" />
                 <ProgramPeek title="Hardware Design" points={["PCB + CAD Enclosures","Fabrication + Assembly","Harnessing + QC"]} badge="In‑Person Labs" />
               </div>
             </div>
           </motion.div>
         </div>
       </div>
     </section>
   );
 }

function Stat({ kpi, label, icon }) {
   return (
     <motion.div
       initial={{ opacity: 0, y: 20 }}
       animate={{ opacity: 1, y: 0 }}
       transition={{ duration: 0.6, delay: 0.8 }}
       className="group relative"
     >
       <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 sm:p-4 border border-slate-200/50 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-105">
         <div className="text-2xl sm:text-3xl mb-2">{icon}</div>
         <div className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-1">{kpi}</div>
         <div className="text-xs uppercase tracking-wider text-slate-500 font-medium leading-tight">{label}</div>
         <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
       </div>
     </motion.div>
   );
 }

function Highlights() {
  const items = [
    {
      title: "Learn by Doing",
      desc: "Hands‑on projects and a live campaign or capstone in every track.",
      image: "/images/backgrounds/learnbydoing.jpg",
      isBackgroundImage: true
    },
    {
      title: "Flexible Delivery",
      desc: "Online, in‑person, or hybrid—weekend and evening options available.",
      image: "/images/backgrounds/Flexible Delivery.jpg",
      isBackgroundImage: true
    },
    {
      title: "Career‑Ready",
      desc: "Graduate with a portfolio, certificate, and job‑ready skills.",
      image: "/images/backgrounds/career ready.png",
      isBackgroundImage: true
    },
  ];
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-4 sm:grid-cols-3">
          {items.map((i, idx) => (
            <motion.div
              key={i.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.05 }}
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${i.isBackgroundImage ? 'relative' : ''}`}
            >
              {i.isBackgroundImage ? (
                <div>
                  <img src={i.image} alt={i.title} className="w-full h-32 object-cover rounded-t-2xl" />
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2">{i.title}</h3>
                    <p className="text-slate-600 text-sm">{i.desc}</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center text-center p-6">
                  <img src={i.image} alt={i.title} className="w-16 h-16 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{i.title}</h3>
                  <p className="text-slate-600 text-sm">{i.desc}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Programs() {
   return (
     <section id="programs" className="py-12 sm:py-16 bg-white">
       <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
         <div className="text-center max-w-2xl mx-auto">
           <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Core Programs (3 Months)</h2>
           <p className="mt-3 text-slate-600 text-sm sm:text-base">Choose your track—each program blends fundamentals with real‑world application.</p>
         </div>

         <div className="mt-8 sm:mt-10 grid gap-6 sm:gap-8 lg:grid-cols-3 lg:max-w-6xl mx-auto">
           <ProgramCard
             colorFrom="from-emerald-500"
             colorTo="to-teal-500"
             title="Digital Marketing"
             duration="3 Months"
             modules={["SEO & Google Ads","Social Media, Content & Email Marketing","Analytics & Reporting","Live Campaign Project"]}
             delivery="Available online or in‑person"
             brochureLink="/digital-marketing-course"
             icon={<Megaphone className="w-8 h-8" />}
           />

           <ProgramCard
             colorFrom="from-violet-600"
             colorTo="to-indigo-600"
             title="AI & Machine Learning"
             duration="3 Months"
             modules={["Python & Data Science Basics","Machine Learning Models & Deep Learning","Generative AI (ChatGPT, Midjourney)","AI Applications in Industry","Capstone Project"]}
             delivery="Theoretical modules available online or in‑person"
             brochureLink="/ai-course"
             icon={<Brain className="w-8 h-8" />}
           />

           <ProgramCard
             colorFrom="from-orange-500"
             colorTo="to-rose-500"
             title="Hardware Design & Manufacturing"
             duration="3 Months"
             modules={["PCB Design & Layout ","3D Enclosure Design (CAD)","PCB Fabrication & Prototyping","Soldering & Assembly Line","Wire Harness Fabrication","PCB Manufacturing & Quality Control"]}
             delivery="In‑person (hands‑on labs)"
             brochureLink="/hardware-course"
             icon={<Cpu className="w-8 h-8" />}
           />

           <ProgramCard
             colorFrom="from-sky-500"
             colorTo="to-blue-500"
             title="Industrial Design and Manufacturing"
             duration="3 Months"
             modules={["Product Design Principles","Prototyping Techniques","Manufacturing Processes","Quality Control Systems","Aerospace Industry Applications","Defence Applications","Healthcare Applications"]}
             delivery="In‑person (hands‑on labs)"
             brochureLink="/aerospace-course"
             icon={<Wrench className="w-8 h-8" />}
           />

           <ProgramCard
             colorFrom="from-cyan-500"
             colorTo="to-teal-500"
             title="SMT Assembly Operator"
             duration="3 Months"
             modules={["Basics of Electronics & Components","SMT Process","Safety Guidelines","Soft Skills"]}
             delivery="In‑person (hands‑on labs)"
             brochureLink="/smt-assembly-course"
             icon={<Cog className="w-8 h-8" />}
           />
         </div>
       </div>
     </section>
   );
 }

function ProgramCard({
    colorFrom,
    colorTo,
    title,
    duration,
    modules,
    delivery,
    brochureLink,
    icon,
  }) {
    return (
      <motion.article
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.45 }}
        className="relative h-full rounded-3xl border border-slate-200 bg-white shadow-sm"
      >
        <div className={`absolute -top-3 sm:-top-4 left-3 sm:left-4 inline-flex items-center rounded-full bg-gradient-to-r ${colorFrom} ${colorTo} px-2 sm:px-3 py-1 text-xs font-semibold text-white shadow`}>{duration}</div>
        <div className="p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-r ${colorFrom} ${colorTo} text-white`}>
              {icon}
            </div>
            <h3 className="text-lg sm:text-xl font-bold">{title}</h3>
          </div>
         <ul className="mt-3 sm:mt-4 space-y-2 text-slate-700">
           {modules.map((m) => (
             <li key={m} className="flex gap-2">
               <span aria-hidden className="mt-1.5 sm:mt-2 h-1.5 w-1.5 rounded-full bg-slate-800 flex-shrink-0" />
               <span className="text-sm sm:text-base leading-tight">{m}</span>
             </li>
           ))}
         </ul>
         <div className="mt-4 sm:mt-5 text-xs sm:text-sm font-semibold text-indigo-700">{delivery}</div>
         <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2 sm:gap-3">
           <Link to="/apply" className="rounded-xl px-3 sm:px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 text-center">Apply</Link>
           {brochureLink ? (
             <Link to={brochureLink} className="rounded-xl px-3 sm:px-4 py-2 text-sm font-semibold border border-slate-300 hover:border-slate-400 text-center">View Brochure</Link>
           ) : (
             <a href="#brochure" className="rounded-xl px-3 sm:px-4 py-2 text-sm font-semibold border border-slate-300 hover:border-slate-400 text-center">View Brochure</a>
           )}
         </div>
       </div>
     </motion.article>
   );
 }

function ProgramPeek({ title, points, badge }) {
   return (
     <div className="relative rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm">
       <div className="absolute right-2 sm:right-3 top-2 sm:top-3 rounded-full bg-indigo-600/10 text-indigo-700 text-xs font-semibold px-2 sm:px-3 py-1">{badge}</div>
       <h4 className="text-sm sm:text-base font-bold pr-16 sm:pr-20">{title}</h4>
       <ul className="mt-2 text-xs sm:text-sm text-slate-700 space-y-1">
         {points.map((p) => (
           <li key={p} className="flex gap-2">
             <span className="mt-1.5 sm:mt-2 h-1 w-1 sm:h-1.5 sm:w-1.5 rounded-full bg-indigo-600 flex-shrink-0" aria-hidden />
             <span className="leading-tight">{p}</span>
           </li>
         ))}
       </ul>
     </div>
   );
 }

function WorkshopsSection() {
   return (
     <section id="workshops" className="py-12 sm:py-16 bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50 relative overflow-hidden">
       {/* Background decorative elements */}
       <div className="absolute inset-0 opacity-5">
         <div className="absolute top-20 left-10 w-32 h-32 bg-indigo-500 rounded-full blur-3xl"></div>
         <div className="absolute bottom-20 right-20 w-48 h-48 bg-violet-500 rounded-full blur-3xl"></div>
       </div>

       <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
         <div className="grid gap-8 sm:gap-12 lg:grid-cols-2 items-center">
           <motion.div
             initial={{ opacity: 0, x: -30 }}
             whileInView={{ opacity: 1, x: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.6 }}
           >
             <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-sm font-semibold mb-4">
               <Clock className="w-4 h-4" />
               3-Month Intensive Programs
             </div>
             <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600 mb-4">
               Hands-On Workshops
             </h2>
             <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-6">
               Intensive 3-month programs combining theory with hands-on projects. Master industry skills with expert mentorship, build portfolio-worthy projects, and launch your career in tech.
             </p>
             <ul className="space-y-3 sm:space-y-4 text-slate-800 mb-8">
               <li className="flex gap-4 items-start">
                 <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 flex items-center justify-center mt-0.5">
                   <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                     <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                   </svg>
                 </div>
                 <span className="text-sm sm:text-base font-medium">AI in Digital Marketing (Every Saturday)</span>
               </li>
               <li className="flex gap-4 items-start">
                 <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center mt-0.5">
                   <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                     <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                   </svg>
                 </div>
                 <span className="text-sm sm:text-base font-medium">Hands-on PCB Prototyping (Every Sunday)</span>
               </li>
               <li className="flex gap-4 items-start">
                 <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center mt-0.5">
                   <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                     <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                   </svg>
                 </div>
                 <span className="text-sm sm:text-base font-medium">Machine Learning Projects (Bi-weekly Wednesdays)</span>
               </li>
             </ul>
             <div className="flex flex-col sm:flex-row gap-4">
               <Link to="/workshops" className="inline-flex items-center justify-center rounded-2xl px-6 py-4 font-bold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-lg hover:shadow-xl transition-all duration-300 text-center text-sm sm:text-base">
                 <Rocket className="w-5 h-5 mr-2" />
                 Reserve a Seat
               </Link>
               <Link to="/workshops#schedule" className="inline-flex items-center justify-center rounded-2xl px-6 py-4 font-bold border-2 border-indigo-200 text-indigo-700 bg-white hover:bg-indigo-50 shadow-md hover:shadow-lg transition-all duration-300 text-center text-sm sm:text-base">
                 <Monitor className="w-5 h-5 mr-2" />
                 View Schedule
               </Link>
             </div>
           </motion.div>

           <motion.div
             initial={{ opacity: 0, x: 30 }}
             whileInView={{ opacity: 1, x: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.6, delay: 0.2 }}
             className="relative mt-8 lg:mt-0"
           >
             <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
               {/* Main workshop image */}
               <img
                 src="/images/backgrounds/Hands-On Workshops.png"
                 alt="Students working on workshop projects"
                 className="w-full h-full object-cover"
               />

               {/* Overlay gradient */}
               <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

               {/* Floating workshop cards */}
               <div className="absolute bottom-6 left-6 right-6 space-y-3">
                 <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
                   <div className="flex items-center gap-3 mb-2">
                     <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-indigo-500 to-violet-500 flex items-center justify-center">
                       <MessageSquare className="w-4 h-4 text-white" />
                     </div>
                     <span className="font-semibold text-slate-900 text-sm">AI + Digital Marketing</span>
                   </div>
                   <p className="text-xs text-slate-600">ChatGPT for Content • AI Analytics • Mini Campaign</p>
                 </div>

                 <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
                   <div className="flex items-center gap-3 mb-2">
                     <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center">
                       <CircuitBoard className="w-4 h-4 text-white" />
                     </div>
                     <span className="font-semibold text-slate-900 text-sm">PCB Prototyping</span>
                   </div>
                   <p className="text-xs text-slate-600">KiCad Schematic • Layout & Routing • Soldering</p>
                 </div>
               </div>
             </div>
           </motion.div>
         </div>
       </div>
     </section>
   );
 }

function WorkshopCard({ title, bullets, icon }) {
    return (
      <div className="rounded-2xl border border-slate-200 p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-slate-600">
            {icon}
          </div>
          <h4 className="font-bold text-sm sm:text-base">{title}</h4>
        </div>
        <ul className="mt-2 text-xs sm:text-sm text-slate-700 space-y-1">
          {bullets.map((b) => (
            <li key={b} className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 sm:h-1.5 sm:w-1.5 rounded-full bg-slate-900 flex-shrink-0" aria-hidden />
              <span className="leading-tight">{b}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

function FlexibleLearning() {
   const options = [
     {
       icon: <Monitor className="w-6 h-6" />,
       title: "Online Learning",
       description: "Complete flexibility with 24/7 access",
       points: [
         "Full Digital Marketing program",
         "AI & ML theory modules",
         "Self-paced learning platform",
         "Mobile-friendly interface"
       ],
       color: "from-blue-500 to-cyan-500"
     },
     {
       icon: <Building className="w-6 h-6" />,
       title: "In-Person Classes",
       description: "Interactive learning with expert instructors",
       points: [
         "Small cohort sizes (8-12 students)",
         "Weekday, weekend & evening batches",
         "Live Q&A sessions",
         "Peer collaboration opportunities"
       ],
       color: "from-emerald-500 to-teal-500"
     },
     {
       icon: <Settings className="w-6 h-6" />,
       title: "Hands-On Labs",
       description: "Industry-grade equipment and facilities",
       points: [
         "Hardware Design & Manufacturing labs",
         "State-of-the-art PCB fabrication",
         "CAD workstations & 3D printers",
         "Professional soldering stations"
       ],
       color: "from-orange-500 to-red-500"
     },
     {
       icon: <Target className="w-6 h-6" />,
       title: "Custom Pathways",
       description: "Mix and match modules to fit your goals",
       points: [
         "Build your own learning journey",
         "Combine tracks as needed",
         "Skip prerequisites with experience",
         "Accelerated or extended timelines"
       ],
       color: "from-purple-500 to-pink-500"
     },
     {
       icon: <Clock className="w-6 h-6" />,
       title: "Flexible Scheduling",
       description: "Learn at your own pace and time",
       points: [
         "Multiple batch timings daily",
         "Weekend intensive programs",
         "Part-time and full-time options",
         "Pause and resume anytime"
       ],
       color: "from-indigo-500 to-blue-500"
     },
     {
       icon: <Globe className="w-6 h-6" />,
       title: "Hybrid Learning",
       description: "Best of both online and in-person worlds",
       points: [
         "Core theory online",
         "Practical labs in-person",
         "Virtual collaboration tools",
         "Global community access"
       ],
       color: "from-violet-500 to-purple-500"
     }
   ];

   return (
     <section id="flex" className="py-12 sm:py-16 bg-gradient-to-br from-slate-50 via-white to-slate-50">
       <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
         <div className="text-center mb-8 sm:mb-12">
           <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Flexible Learning Options</h2>
           <p className="mt-3 sm:mt-4 text-base sm:text-lg text-slate-600 max-w-3xl mx-auto">
             Choose the learning style that fits your lifestyle. From fully online to hands-on labs,
             we offer multiple pathways to help you achieve your career goals.
           </p>
         </div>

         <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
           {options.map((option, index) => (
             <motion.div
               key={option.title}
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ duration: 0.5, delay: index * 0.1 }}
               className="group"
             >
               <div className="relative h-full rounded-3xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden">
                 {/* Background gradient on hover */}
                 <div className={`absolute inset-0 bg-gradient-to-br ${option.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />

                 <div className="relative">
                   {/* Icon and title */}
                   <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-4">
                     <div className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br ${option.color} text-white text-xl sm:text-2xl`}>
                       {option.icon}
                     </div>
                     <div className="text-center sm:text-left">
                       <h3 className="text-lg sm:text-xl font-bold text-slate-900">{option.title}</h3>
                       <p className="text-xs sm:text-sm text-slate-600">{option.description}</p>
                     </div>
                   </div>

                   {/* Points */}
                   <ul className="space-y-2 sm:space-y-3">
                     {option.points.map((point, i) => (
                       <li key={i} className="flex items-start gap-3">
                         <div className={`flex-shrink-0 w-2 h-2 rounded-full bg-gradient-to-r ${option.color} mt-1.5 sm:mt-2`} />
                         <span className="text-slate-700 leading-relaxed text-sm sm:text-base">{point}</span>
                       </li>
                     ))}
                   </ul>

                   {/* Hover indicator */}
                   <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                     <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                     </svg>
                   </div>
                 </div>
               </div>
             </motion.div>
           ))}
         </div>

         {/* Call to action */}
         <div className="mt-8 sm:mt-12 text-center">
           <p className="text-slate-600 mb-4 sm:mb-6 text-sm sm:text-base">Not sure which option is right for you?</p>
           <div className="flex flex-col sm:flex-row gap-4 justify-center">
             <Link
               to="/apply"
               className="inline-flex items-center justify-center px-4 sm:px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold rounded-2xl hover:shadow-lg transition text-sm sm:text-base"
             >
               Schedule a Consultation
             </Link>
           </div>
         </div>
       </div>
     </section>
   );
 }

function WhyChooseNCAI() {
  return (
    <section className="relative py-20 sm:py-24 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/backgrounds/Generated Image October 09, 2025 - 3_10AM.png"
          alt="Students collaborating in modern classroom"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/90 via-violet-900/85 to-purple-900/90" />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-6">
              Why Choose <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-400">NCAI</span>?
            </h2>
            <p className="text-lg sm:text-xl text-indigo-100 max-w-3xl mx-auto leading-relaxed">
              Join India's premier institute for cutting-edge technology education. We're not just teaching skills—we're shaping the future of innovation.
            </p>
          </motion.div>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-xl flex items-center justify-center mb-4">
              <Target className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Industry-Aligned Curriculum</h3>
            <p className="text-indigo-100 leading-relaxed">
              Our programs are designed with input from industry leaders, ensuring you learn the most relevant and in-demand skills.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-violet-400 to-purple-400 rounded-xl flex items-center justify-center mb-4">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Expert Faculty</h3>
            <p className="text-indigo-100 leading-relaxed">
              Learn from industry veterans and academic experts who bring real-world experience to every classroom.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 md:col-span-2 lg:col-span-1"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-xl flex items-center justify-center mb-4">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Career Acceleration</h3>
            <p className="text-indigo-100 leading-relaxed">
              From learning to landing your dream job in just 3 months. Our career support ensures your success.
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <div className="inline-flex items-center gap-6 bg-white/15 backdrop-blur-sm rounded-2xl px-8 py-6 border border-white/20">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">500+</div>
              <div className="text-sm text-indigo-200">Students Trained</div>
            </div>
            <div className="w-px h-12 bg-white/30"></div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">95%</div>
              <div className="text-sm text-indigo-200">Placement Rate</div>
            </div>
            <div className="w-px h-12 bg-white/30"></div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">50+</div>
              <div className="text-sm text-indigo-200">Partner Companies</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Outcomes() {
   const items = [
     {
       icon: <Target className="w-8 h-8" />,
       title: "Portfolio‑Ready",
       desc: "Showcase a live campaign or capstone with code, designs, and reports.",
       gradient: "from-emerald-500 to-teal-500",
       bgPattern: "bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))]"
     },
     {
       icon: <Wrench className="w-8 h-8" />,
       title: "Industry Tools",
       desc: "Master SMT assembly lines, PCB fabrication machines, 3D printers, CNC machines, and professional soldering stations.",
       gradient: "from-violet-500 to-purple-500",
       bgPattern: "bg-[conic-gradient(at_top,_var(--tw-gradient-stops))]"
     },
     {
       icon: <Rocket className="w-8 h-8" />,
       title: "Career Support",
       desc: "Interview prep, resume polish, and project reviews to stand out.",
       gradient: "from-orange-500 to-red-500",
       bgPattern: "bg-[linear-gradient(135deg,_var(--tw-gradient-stops))]"
     },
   ];
   return (
     <section id="outcomes" className="py-12 sm:py-16 bg-gradient-to-br from-slate-50 via-white to-slate-50">
       <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
         <motion.div
           initial={{ opacity: 0, y: 20 }}
           whileInView={{ opacity: 1, y: 0 }}
           viewport={{ once: true }}
           transition={{ duration: 0.6 }}
           className="text-center mb-8 sm:mb-12"
         >
           <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-indigo-600">
             What You’ll Achieve
           </h2>
           <p className="mt-3 sm:mt-4 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
             Graduate job‑ready with practical work that proves your skills and opens doors to exciting career opportunities.
           </p>
         </motion.div>

         <div className="grid gap-4 sm:gap-8 md:grid-cols-3">
           {items.map((i, index) => (
             <motion.div
               key={i.title}
               initial={{ opacity: 0, y: 30 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ duration: 0.6, delay: index * 0.2 }}
               className="group relative"
             >
               <div className="relative h-full rounded-3xl border border-slate-200/60 bg-white p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                 {/* Animated background gradient */}
                 <div className={`absolute inset-0 bg-gradient-to-br ${i.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 ${i.bgPattern}`} />

                 {/* Floating particles effect */}
                 <div className="absolute top-4 right-4 w-16 h-16 sm:w-20 sm:h-20 opacity-10 group-hover:opacity-20 transition-opacity duration-500">
                   <div className="absolute w-2 h-2 bg-current rounded-full animate-ping" style={{ animationDelay: '0s', animationDuration: '3s' }} />
                   <div className="absolute w-1 h-1 bg-current rounded-full animate-ping" style={{ animationDelay: '1s', animationDuration: '2s', top: '10px', left: '15px' }} />
                   <div className="absolute w-1.5 h-1.5 bg-current rounded-full animate-ping" style={{ animationDelay: '2s', animationDuration: '2.5s', bottom: '5px', right: '10px' }} />
                 </div>

                 <div className="relative">
                   {/* Icon */}
                   <motion.div
                     className={`inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${i.gradient} text-white text-2xl sm:text-3xl mb-4 sm:mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                     whileHover={{ rotate: 5 }}
                   >
                     {i.icon}
                   </motion.div>

                   {/* Title */}
                   <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 group-hover:text-indigo-600 transition-colors duration-300">
                     {i.title}
                   </h3>

                   {/* Description */}
                   <p className="text-slate-600 leading-relaxed group-hover:text-slate-700 transition-colors duration-300 text-sm sm:text-base">
                     {i.desc}
                   </p>

                   {/* Hover indicator */}
                   <motion.div
                     className="absolute bottom-4 sm:bottom-6 right-4 sm:right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                     initial={{ scale: 0 }}
                     whileHover={{ scale: 1 }}
                   >
                     <svg className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                     </svg>
                   </motion.div>
                 </div>
               </div>
             </motion.div>
           ))}
         </div>

         {/* Additional visual element */}
         <motion.div
           initial={{ opacity: 0, scale: 0.8 }}
           whileInView={{ opacity: 1, scale: 1 }}
           viewport={{ once: true }}
           transition={{ duration: 0.8, delay: 0.6 }}
           className="mt-12 sm:mt-16 text-center"
         >
           <div className="inline-flex items-center gap-3 sm:gap-4 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-violet-50 to-indigo-50 rounded-full border border-violet-200/50">
             <div className="flex -space-x-2">
               <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 border-2 border-white"></div>
               <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-r from-violet-400 to-purple-400 border-2 border-white"></div>
               <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-r from-orange-400 to-red-400 border-2 border-white"></div>
             </div>
             <span className="text-xs sm:text-sm font-semibold text-slate-700">Join 500+ successful graduates</span>
           </div>
         </motion.div>
       </div>
     </section>
   );
 }

function Testimonials() {
  const testimonials = [
    {
      name: "Arjun Kumar",
      role: "Digital Marketing Specialist",
      company: "TechCorp Solutions",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      quote: "I was working as a sales executive when I joined NCAI's digital marketing course. The way they explained Google Ads and SEO made everything click for me. Within 2 months, I got promoted and now I manage our entire digital campaigns. The live project we did for a real client gave me the confidence I needed.",
      rating: 5
    },
    {
      name: "Meera Patel",
      role: "Hardware Engineer",
      company: "InnovateLabs",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      quote: "As a fresh engineering graduate, I had theoretical knowledge but no practical experience. NCAI's hardware design course changed that completely. The SMT assembly training and PCB design labs were incredible. I built my first complete electronic device during the course, and that portfolio helped me get a job at a top electronics company.",
      rating: 5
    },
    {
      name: "Rahul Singh",
      role: "AI Developer",
      company: "DataFlow Inc",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      quote: "I always wanted to learn AI but was scared of the complex math. NCAI made it so approachable. The Python basics to deep learning journey was amazing. What I loved most was how we built real AI applications - a chatbot, recommendation system, and even worked on computer vision. Got placed as an AI developer with a 40% salary hike!",
      rating: 5
    },
    {
      name: "Ananya Gupta",
      role: "Marketing Analyst",
      company: "GrowthMetrics",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      quote: "Coming from a non-tech background, I thought digital marketing was just about social media posts. NCAI opened my eyes to the entire ecosystem - analytics, SEO, content strategy, and paid ads. The best part was our live campaign project where we managed a client's ₹50 lakh ad budget. That experience was priceless and got me my current role.",
      rating: 5
    }
  ];

  return (
    <section className="py-12 sm:py-16 bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-indigo-600">
            What Our Students Say
          </h2>
          <p className="mt-3 sm:mt-4 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            Hear from our successful graduates who have launched their careers in tech.
          </p>
        </motion.div>

        <div className="grid gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-2">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative"
            >
              <div className="relative h-full rounded-3xl border border-slate-200/60 bg-white p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                {/* Background gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-violet-50/50 to-indigo-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Quote icon */}
                <div className="absolute -top-4 left-6 sm:left-8">
                  <div className="w-8 h-8 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z" />
                    </svg>
                  </div>
                </div>

                {/* Rating stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>

                {/* Testimonial text */}
                <blockquote className="text-slate-700 mb-6 text-sm sm:text-base leading-relaxed font-medium">
                  "{testimonial.quote}"
                </blockquote>

                {/* Author info */}
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-3 border-white shadow-lg"
                    />
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-slate-900 text-base">{testimonial.name}</div>
                    <div className="text-sm text-slate-600 font-medium">{testimonial.role}</div>
                    <div className="text-sm text-indigo-600 font-semibold">{testimonial.company}</div>
                  </div>
                </div>

                {/* Decorative element */}
                <div className="absolute bottom-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity duration-300">
                  <svg className="w-12 h-12 text-indigo-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 sm:mt-16 text-center"
        >
          <div className="inline-flex items-center gap-4 px-6 sm:px-8 py-4 bg-gradient-to-r from-violet-50 to-indigo-50 rounded-full border border-violet-200/50 shadow-sm">
            <div className="flex -space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 border-2 border-white shadow-sm"></div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-violet-400 to-purple-400 border-2 border-white shadow-sm"></div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-orange-400 to-red-400 border-2 border-white shadow-sm"></div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-blue-400 to-cyan-400 border-2 border-white shadow-sm"></div>
            </div>
            <span className="text-sm sm:text-base font-semibold text-slate-700">Join 500+ successful graduates</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}


function CallToAction() {
   return (
     <section id="apply" className="py-12 sm:py-16">
       <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
         <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-r from-violet-600 to-indigo-600 p-6 sm:p-8 lg:p-12 text-white shadow-xl">
           <div className="absolute -inset-10 opacity-25 blur-2xl bg-[radial-gradient(60%_60%_at_50%_40%,white,transparent_60%)]" />
           <div className="relative grid gap-4 sm:gap-6 lg:grid-cols-[1fr_auto] items-center">
             <div>
               <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Admissions Open</h2>
               <p className="mt-2 text-indigo-100 max-w-2xl text-sm sm:text-base">Kick‑start your journey in Digital Marketing, AI & ML, or Hardware Design. Limited seats for small, mentor‑led cohorts.</p>
             </div>
             <div className="flex flex-col sm:flex-row gap-3">
               <Link to="/apply" className="inline-flex items-center justify-center rounded-2xl px-4 sm:px-5 py-3 font-semibold bg-white text-slate-900 hover:bg-slate-50 text-sm sm:text-base">Start Application</Link>
               <Link to="/brochure" className="inline-flex items-center justify-center rounded-2xl px-4 sm:px-5 py-3 font-semibold bg-white/10 ring-1 ring-white/40 hover:bg-white/20 text-sm sm:text-base">Download Brochure</Link>
             </div>
           </div>
         </div>
       </div>
     </section>
   );
 }

function SiteFooter() {
   return (
     <footer className="border-t border-slate-200 bg-slate-50">
       <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
         <div className="grid gap-6 sm:gap-8 lg:grid-cols-4 md:grid-cols-2">
           {/* Brand Section */}
           <div className="lg:col-span-1">
             <Link to="/" className="flex items-center gap-2 sm:gap-3 mb-4">
               <Logo />
               <span className="text-xs sm:text-sm font-semibold text-slate-700">NeuraCore AI Institute</span>
             </Link>
             <p className="text-xs sm:text-sm text-slate-600 mb-4">
               Empowering careers in Digital Marketing, AI & Machine Learning, and Hardware Design & Manufacturing.
             </p>
             <div className="flex gap-3 sm:gap-4">
               <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors">
                 <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
                   <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                 </svg>
               </a>
               <a href="https://linkedin.com/in/neuracore-ai-institute-ncai-727636389" className="text-slate-400 hover:text-indigo-600 transition-colors">
                 <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
                   <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                 </svg>
               </a>
               <a href="https://www.instagram.com/neuracore_ai_institute?igsh=aThjdG5pa3Z5NHV0" className="text-slate-400 hover:text-indigo-600 transition-colors">
                 <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
                   <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                 </svg>
               </a>
             </div>
           </div>

           {/* Quick Links */}
           <div>
             <h3 className="text-xs sm:text-sm font-semibold text-slate-900 mb-3 sm:mb-4">Quick Links</h3>
             <ul className="space-y-2">
               <li><a href="#programs" className="text-xs sm:text-sm text-slate-600 hover:text-indigo-600 transition-colors">Programs</a></li>
               <li><Link to="/workshops" className="text-xs sm:text-sm text-slate-600 hover:text-indigo-600 transition-colors">Workshops</Link></li>
               <li><a href="#flex" className="text-xs sm:text-sm text-slate-600 hover:text-indigo-600 transition-colors">Flexible Learning</a></li>
               <li><a href="#outcomes" className="text-xs sm:text-sm text-slate-600 hover:text-indigo-600 transition-colors">Outcomes</a></li>
             </ul>
           </div>

           {/* Contact Info */}
           <div>
             <h3 className="text-xs sm:text-sm font-semibold text-slate-900 mb-3 sm:mb-4">Contact Us</h3>
             <ul className="space-y-2 text-xs sm:text-sm text-slate-600">
               <li className="flex items-center gap-2">
                 <span className="text-slate-400">📧</span>
                 <a href="mailto:info@ncai.in" className="hover:text-indigo-600 transition-colors">info@ncai.in</a>
               </li>
               <li className="flex items-center gap-2">
                 <span className="text-slate-400">📞</span>
                 <a href="tel:+917356011039" className="hover:text-indigo-600 transition-colors">7356011039</a>
               </li>
               <li className="flex items-start gap-2">
                 <span className="text-slate-400 mt-0.5">📍</span>
                 <span>Opposite to John Cox Engineer College,<br />Kannanmoola, Trivandrum</span>
               </li>
             </ul>
           </div>

           {/* Newsletter */}
           <div>
             <h3 className="text-xs sm:text-sm font-semibold text-slate-900 mb-3 sm:mb-4">Stay Updated</h3>
             <p className="text-xs sm:text-sm text-slate-600 mb-3 sm:mb-4">Subscribe to our newsletter for the latest updates and insights.</p>
             <form className="space-y-2">
               <input
                 type="email"
                 placeholder="Enter your email"
                 className="w-full px-3 py-2 text-xs sm:text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
               />
               <button
                 type="submit"
                 className="w-full px-3 py-2 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 rounded-lg hover:shadow-md transition"
               >
                 Subscribe
               </button>
             </form>
           </div>
         </div>

         <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-slate-200">
           <div className="flex flex-col md:flex-row items-center justify-between gap-4">
             <p className="text-xs sm:text-sm text-slate-500">© {new Date().getFullYear()} NeuraCore AI Institute (NCAI). All rights reserved.</p>
             <div className="flex gap-4 sm:gap-6 text-xs sm:text-sm text-slate-500">
               <Link to="/privacy-policy" className="hover:text-indigo-600 transition-colors">Privacy Policy</Link>
               <Link to="/terms-and-conditions" className="hover:text-indigo-600 transition-colors">Terms & Conditions</Link>
             </div>
           </div>
         </div>
       </div>
     </footer>
   );
 }

function WhatsAppChat() {
  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 1, type: "spring", stiffness: 260, damping: 20 }}
      className="fixed bottom-6 right-6 z-50"
    >
      <a
        href="https://wa.me/7356011039?text=Hi%20NCAI,%20I'm%20interested%20in%20your%20courses"
        target="_blank"
        rel="noopener noreferrer"
        className="group relative flex items-center justify-center w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
      >
        {/* Pulse animation */}
        <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-20"></div>

        {/* WhatsApp icon */}
        <svg
          className="w-7 h-7 text-white relative z-10"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
        </svg>

        {/* Tooltip */}
        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
          Chat with us on WhatsApp
          <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
        </div>
      </a>
    </motion.div>
  );
}

function PrivacyPolicy() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900"
    >
      <SiteHeader />
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-indigo-600">
            Privacy Policy
          </h1>
          <p className="mt-4 text-lg text-slate-600">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </motion.div>

        <div className="prose prose-slate max-w-none">
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Information We Collect</h2>
            <p className="text-slate-700 mb-4">
              We collect information you provide directly to us, such as when you create an account, apply for our courses,
              subscribe to our newsletter, or contact us for support. This may include:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Name, email address, phone number, and other contact information</li>
              <li>Educational background and professional experience</li>
              <li>Payment information for course enrollment</li>
              <li>Communications you send to us</li>
              <li>Information from social media platforms if you connect them to our services</li>
            </ul>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. How We Use Your Information</h2>
            <p className="text-slate-700 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Provide, maintain, and improve our educational services</li>
              <li>Process applications and enrollments</li>
              <li>Send you technical notices, updates, and support messages</li>
              <li>Respond to your comments, questions, and requests</li>
              <li>Send you newsletters and marketing communications (with your consent)</li>
              <li>Monitor and analyze trends and usage</li>
              <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
            </ul>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Information Sharing and Disclosure</h2>
            <p className="text-slate-700 mb-4">
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent,
              except as described in this policy:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>With service providers who assist us in operating our platform</li>
              <li>To comply with legal obligations</li>
              <li>To protect our rights and prevent fraud</li>
              <li>In connection with a business transfer or acquisition</li>
            </ul>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Data Security</h2>
            <p className="text-slate-700">
              We implement appropriate technical and organizational measures to protect your personal information against
              unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the
              internet is 100% secure.
            </p>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Your Rights</h2>
            <p className="text-slate-700 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Access, update, or delete your personal information</li>
              <li>Object to or restrict certain processing of your information</li>
              <li>Data portability</li>
              <li>Withdraw consent where applicable</li>
            </ul>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Cookies and Tracking</h2>
            <p className="text-slate-700">
              We use cookies and similar technologies to enhance your experience, analyze usage, and assist in our
              marketing efforts. You can control cookie preferences through your browser settings.
            </p>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Contact Us</h2>
            <p className="text-slate-700">
              If you have any questions about this Privacy Policy, please contact us at{' '}
              <a href="mailto:info@ncai.in" className="text-indigo-600 hover:text-indigo-800">
                info@ncai.in
              </a>
              {' '}or call us at{' '}
              <a href="tel:+917356011039" className="text-indigo-600 hover:text-indigo-800">
                7356011039
              </a>.
            </p>
          </motion.section>
        </div>
      </div>
      <SiteFooter />
    </motion.div>
  );
}

function TermsAndConditions() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900"
    >
      <SiteHeader />
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-indigo-600">
            Terms and Conditions
          </h1>
          <p className="mt-4 text-lg text-slate-600">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </motion.div>

        <div className="prose prose-slate max-w-none">
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-700">
              By accessing and using NeuraCore AI Institute (NCAI) services, you accept and agree to be bound by the
              terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
            </p>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Course Enrollment and Payment</h2>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>All course fees must be paid in full before the start of classes</li>
              <li>Payment plans may be available at the discretion of NCAI</li>
              <li>Refunds are subject to our refund policy outlined in the enrollment agreement</li>
              <li>Course fees are non-transferable to other individuals</li>
            </ul>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Intellectual Property</h2>
            <p className="text-slate-700 mb-4">
              All course materials, content, and resources provided by NCAI are protected by copyright and intellectual
              property laws. Students may use materials for personal educational purposes only.
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Course materials may not be copied, distributed, or shared without permission</li>
              <li>Projects and assignments completed during courses remain the intellectual property of the student</li>
              <li>NCAI reserves the right to use anonymized student work for promotional purposes</li>
            </ul>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Student Conduct</h2>
            <p className="text-slate-700 mb-4">Students are expected to:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Maintain respectful and professional behavior towards instructors and fellow students</li>
              <li>Complete assignments and participate actively in course activities</li>
              <li>Respect intellectual property rights and academic integrity</li>
              <li>Follow all classroom and lab safety guidelines</li>
            </ul>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Refund Policy</h2>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>14-day money-back guarantee from the start of the program</li>
              <li>Refunds will be processed within 10-14 business days</li>
              <li>Partial refunds may be available for extenuating circumstances</li>
              <li>No refunds for completed courses or certifications already issued</li>
            </ul>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Limitation of Liability</h2>
            <p className="text-slate-700">
              NCAI shall not be liable for any indirect, incidental, special, or consequential damages arising out of
              or in connection with the use of our services. Our total liability shall not exceed the amount paid for the course.
            </p>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Changes to Terms</h2>
            <p className="text-slate-700">
              We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting
              on our website. Your continued use of our services constitutes acceptance of the modified terms.
            </p>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Contact Information</h2>
            <p className="text-slate-700">
              For questions about these Terms and Conditions, please contact us at{' '}
              <a href="mailto:info@ncai.in" className="text-indigo-600 hover:text-indigo-800">
                info@ncai.in
              </a>
              {' '}or call us at{' '}
              <a href="tel:+917356011039" className="text-indigo-600 hover:text-indigo-800">
                7356011039
              </a>.
            </p>
          </motion.section>
        </div>
      </div>
      <SiteFooter />
    </motion.div>
  );
}